// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { InterviewLinkResponse } from '../../../../store/interviewHostSlice';
import styles from './candidates.module.scss';

interface InterviewLinkModalProps {
  open: boolean;
  onClose: () => void;
  interviewData: InterviewLinkResponse;
}

const InterviewLinkModal: React.FC<InterviewLinkModalProps> = ({ open, onClose, interviewData }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(interviewData.interview_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Interview Link Sent</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.successMessage}>
            <CheckCircleIcon className={styles.successIcon} />
            <p>
              Interview link has been successfully sent to{' '}
              <strong>{interviewData.recruiter_context.candidate_name}</strong>
            </p>
          </div>

          <div className={styles.interviewDetails}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Session ID:</span>
              <span className={styles.detailValue}>{interviewData.session_id}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Duration:</span>
              <span className={styles.detailValue}>{interviewData.interview_duration} minutes</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Language:</span>
              <span className={styles.detailValue}>{interviewData.language}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Status:</span>
              <span className={`${styles.detailValue} ${styles.statusInvited}`}>{interviewData.status}</span>
            </div>
            {interviewData.email_status && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  <EmailIcon sx={{ fontSize: 16, verticalAlign: 'middle', marginRight: '0.25rem' }} />
                  Email Status:
                </span>
                <span className={styles.detailValue}>
                  {interviewData.email_status === 'mock_sent' ? 'Sent (Mock)' : interviewData.email_status}
                </span>
              </div>
            )}
          </div>

          <div className={styles.linkSection}>
            <label className={styles.linkLabel}>Interview Link</label>
            <div className={styles.linkBox}>
              <input
                type="text"
                className={styles.linkInput}
                value={interviewData.interview_link}
                readOnly
                onClick={(e) => e.currentTarget.select()}
              />
              <button className={copied ? styles.btnCopied : styles.btnCopy} onClick={handleCopyLink}>
                {copied ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                    Copied
                  </>
                ) : (
                  <>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className={styles.linkHint}>Share this link with the candidate to start their interview</p>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnPrimary} onClick={onClose}>
            Done
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default InterviewLinkModal;
